import type { CashAccountChoice, CreateCashVoucherInput } from '../api/cash';
import type { CashVoucherKind, PaymentMethod } from '../generated/enums';
import { canAct } from '../session/capability';
import type { Session } from '../session/types';
import { expenseAmount, expenseMethods } from './expense';
import { paidFromProblem } from './paidFrom';
import { filledParts, splitProblem, type SplitPart } from './split';

/**
 * Money in and money out that is not a booking, an expense or a vendor payment — owner's money,
 * owner's drawings, other income (Cash & bank step 4, `docs/Cash_Bank_Flow_Design.md` §3.2).
 *
 * <p>
 * <strong>Every refusal here is the server's, mirrored</strong>: an amount above zero, a day not
 * yet come, never `Adjustment`, drawings saying what for, an account that holds what the method
 * moves. <strong>Which kinds a person is offered is their permission</strong>: owner's money is the
 * company administrator's (`OwnersMoney`, owner 2026-10-22), other income Cash & bank's.
 * </p>
 */

/** What each kind is called on a tile and in a list. */
export const voucherKindWords: Record<CashVoucherKind, string> = {
  OwnerIn: 'Owner’s money in',
  OwnerOut: 'Owner’s drawings',
  OtherIncome: 'Other income',
};

/** The sentence under each tile — what the kind is for, and what it is not. */
export const voucherKindHints: Record<CashVoucherKind, string> = {
  OwnerIn: 'Money the owner or a partner puts into the business. Not income.',
  OwnerOut: 'Money the owner or a partner takes home. Not an expense.',
  OtherIncome:
    'Money with nothing sold behind it — bank interest, a refunded deposit. Anything sold goes through a sale bill.',
};

export type VoucherSide = 'in' | 'out';

/** Which way the money moves for a kind. */
export function sideOf(kind: CashVoucherKind): VoucherSide {
  return kind === 'OwnerOut' ? 'out' : 'in';
}

/** The kinds a person may record on one side — none means the button is not offered. */
export function voucherKindsFor(session: Session, side: VoucherSide): CashVoucherKind[] {
  const owner = canAct(session, 'OwnersMoney:Create');
  const cash = canAct(session, 'CashAndBank:Create');

  if (side === 'out') {
    return owner ? ['OwnerOut'] : [];
  }

  return [...(owner ? (['OwnerIn'] as const) : []), ...(cash ? (['OtherIncome'] as const) : [])];
}

/** Whether a person may void a voucher of this kind — the same doors as recording one. */
export function mayVoidVoucher(session: Session, kind: CashVoucherKind): boolean {
  return canAct(session, kind === 'OtherIncome' ? 'CashAndBank:Edit' : 'OwnersMoney:Edit');
}

/** Every way money can move but `Adjustment`, which moves none. */
export const voucherMethods: readonly PaymentMethod[] = expenseMethods;

export interface VoucherDraft {
  kind: CashVoucherKind;
  amount: string;
  occurredOn: string;
  method: PaymentMethod;
  /** Whose money; blank on owner's money is the company owner. */
  personName: string;
  reference: string;
  notes: string;
  /** Null: the method's default account. */
  cashAccountId: number | null;
}

export function emptyVoucher(kind: CashVoucherKind, today: string): VoucherDraft {
  return {
    kind,
    amount: '',
    occurredOn: today,
    method: kind === 'OwnerOut' ? 'Cash' : 'BankTransfer',
    personName: '',
    reference: '',
    notes: '',
    cashAccountId: null,
  };
}

/** The one thing standing between the form and Save, or null — most-blocking first. */
export function voucherProblem(
  draft: VoucherDraft,
  today: string,
  accounts?: readonly CashAccountChoice[],
): string | null {
  if (expenseAmount(draft.amount) === null) {
    return 'Type how much moved.';
  }

  if (draft.method === 'Adjustment') {
    return 'An adjustment moves no money. Choose how it was paid.';
  }

  if (draft.occurredOn === '') {
    return 'Say which day the money moved.';
  }

  if (draft.occurredOn > today) {
    return 'A day that has not happened yet cannot be recorded.';
  }

  if (draft.kind === 'OwnerOut' && draft.notes.trim() === '') {
    return 'Say what the drawings were for.';
  }

  return paidFromProblem(draft.cashAccountId, draft.method, accounts);
}

/** The request, or null while there is a problem. */
export function voucherRequest(
  draft: VoucherDraft,
  today: string,
  accounts?: readonly CashAccountChoice[],
): CreateCashVoucherInput | null {
  const amount = expenseAmount(draft.amount);

  if (voucherProblem(draft, today, accounts) !== null || amount === null) {
    return null;
  }

  return {
    kind: draft.kind,
    amount,
    occurredOn: draft.occurredOn,
    method: draft.method,
    personName: draft.kind === 'OtherIncome' ? null : draft.personName.trim() || null,
    reference: draft.reference.trim() || null,
    notes: draft.notes.trim() || null,
    cashAccountId: draft.cashAccountId,
  };
}

/* ------------------------------------------------------------- split across accounts */

/**
 * What stops a voucher split across accounts being saved (split payments step 6, owner 2026-11-01:
 * several vouchers, one per account): each box a figure, a day that has come, and drawings saying
 * what for. `direction` words the empty-boxes line — "into" for money in, "from" for drawings.
 */
export function voucherPartsProblem(
  draft: VoucherDraft,
  today: string,
  parts: readonly SplitPart[],
): string | null {
  const boxes = splitProblem(parts, draft.kind === 'OwnerOut' ? 'from' : 'into');

  if (boxes) {
    return boxes;
  }

  const bad = filledParts(parts).find((part) => expenseAmount(part.amount) === null);

  if (bad) {
    return `The amount for ${bad.accountName} must be a figure above zero, to the paisa at most.`;
  }

  if (draft.occurredOn === '') {
    return 'Say which day the money moved.';
  }

  if (draft.occurredOn > today) {
    return 'A day that has not happened yet cannot be recorded.';
  }

  return draft.kind === 'OwnerOut' && draft.notes.trim() === ''
    ? 'Say what the drawings were for.'
    : null;
}

/** Everything a voucher carries but the account, amount and how — the same on every part. */
export function voucherBase(draft: VoucherDraft) {
  return {
    kind: draft.kind,
    occurredOn: draft.occurredOn,
    personName: draft.kind === 'OtherIncome' ? null : draft.personName.trim() || null,
    reference: draft.reference.trim() || null,
    notes: draft.notes.trim() || null,
  };
}
